import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Lock, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalSessions = () => {
  const { user, loading } = useAuth();
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user?.email) {
      fetch(`${API_URL}/api/blueprint/subscription/${user.email}`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => setSubscription(d))
        .catch(() => {});
    }
  }, [user?.email]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2CA8A0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/portal" replace />;

  const tier = subscription?.tier || 'none';
  const hasCallAccess = ['navigator', 'concierge', 'blueprint_only'].includes(tier);
  const hasConcierge = tier === 'concierge';

  return (
    <PortalLayout activeTab="sessions">
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="mb-10">
          <h1 className="text-3xl font-light text-white mb-2" data-testid="sessions-title">Sessions</h1>
          <p className="text-[#888]">Schedule and manage your advisory sessions.</p>
        </div>

        {hasCallAccess ? (
          <>
            <div className="bg-[#F9F6EF] border border-[#FFFFFF] p-6 sm:p-8 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#FFFFFF] flex items-center justify-center rounded">
                      <Calendar className="w-5 h-5 text-[#2CA8A0]" />
                    </div>
                    <h2 className="text-lg font-medium text-white">Book Advisory Session</h2>
                  </div>
                  <p className="text-[#888] text-sm max-w-lg">
                    Schedule a one-on-one session with your Mundalo advisor to discuss your cross-border transition,
                    review documentation, or address specific questions.
                  </p>
                </div>
                <a
                  href="https://cal.com/mundalo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#2CA8A0] text-black px-6 py-3 text-sm font-medium hover:bg-[#2CA8A0] transition-all group"
                  data-testid="book-session-btn"
                >
                  Schedule Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {hasConcierge && (
              <div className="bg-[#F9F6EF] border border-[#2CA8A0]/20 p-6 sm:p-8 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <MessageCircle className="w-5 h-5 text-[#2CA8A0]" />
                  <h2 className="text-lg font-medium text-white">Direct WhatsApp Access</h2>
                </div>
                <p className="text-[#888] text-sm mb-4">As a Concierge client, you have direct WhatsApp access to the Mundalo team for urgent questions and real-time guidance.</p>
                <a
                  href="https://wa.me/525554567678?text=Hi%20Mundalo%20team%2C%20I%27m%20a%20Concierge%20client."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-medium hover:bg-[#20bd5a] transition-all"
                  data-testid="whatsapp-direct-btn"
                >
                  Message on WhatsApp
                </a>
              </div>
            )}
          </>
        ) : (
          <div className="bg-[#F9F6EF] border border-[#FFFFFF] p-8 text-center">
            <Lock className="w-10 h-10 text-[#333] mx-auto mb-4" />
            <h2 className="text-white text-lg mb-2">Advisory sessions are available on Navigator and Concierge plans</h2>
            <p className="text-[#666] text-sm mb-6">Upgrade your plan to book sessions with your Mundalo advisor.</p>
            <a href="/portal/subscription" className="inline-flex items-center gap-2 bg-[#2CA8A0] text-black px-6 py-3 text-sm font-medium hover:bg-[#2CA8A0] transition-all">
              View Plans <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        <div className="bg-[#F9F6EF] border border-[#FFFFFF] p-8">
          <h2 className="text-sm text-[#666] uppercase tracking-wider mb-6">Session History</h2>
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="session-history-empty">
            <Clock className="w-12 h-12 text-[#333] mb-4" />
            <p className="text-white mb-2">No sessions yet</p>
            <p className="text-[#666] text-sm">Your session history will appear here.</p>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalSessions;
